import { createMailCryptoClient } from '@platform/api-client';
/**
 * `WebmailApi` against services/platform-api (EWS behind it). Maps the API shapes to the
 * webmail model. Composer bodies go out as sanitizable mail HTML with inline images (or plain text);
 * in replies/forwards the body already holds the quoted history (one copy, built by the composer).
 * Attachments are uploaded while composing and referenced by id on send.
 */
import { expirePwa, expireOwnedSession, markOwnedSession, onPeerExpiry } from '../pwa';
import {
  ApiError,
  createHttpClient,
  createGroupwareClient,
  createMfaClient,
  createIdentityClient,
  logoutBrowser,
  createPublicClient,
  createWebmailClient,
  type AttachedMessage as ApiAttachedMessage,
  type ItemResults,
  type ItemSummary,
  type MailAddress,
  type MailFolder,
  type MessageDetail,
  type NewMessage,
  type SessionInfo,
} from '@platform/api-client';
import { PARTIAL_FAILURE, plainTextToMailHtml, prepareOutgoingHtml, type InlineImage, type PartialFailure, type RichTextValue } from '@platform/ui';
import { PAGE_SIZE, SOURCE_VIEW_BYTES } from './constants';
import { loadDraftHtml } from './draft-html';
import { createRealCalendarExtra } from './calendar-extra';
import { createRealQuarantineMe } from './quarantine';
import { createRealCalendarFeeds } from './calendar-feeds';
import type { WebmailApi } from './index';
import type { Address, AttachedMessage, Folder, Message, MessageSummary, Session, WellKnownFolder } from './types';

/** API well-known folder names → app names (used for icons and localized labels). */
const WELL_KNOWN: Readonly<Record<NonNullable<MailFolder['wellKnown']>, WellKnownFolder>> = {
  inbox: 'inbox',
  drafts: 'drafts',
  sentitems: 'sent',
  deleteditems: 'deleted',
  junkemail: 'junk',
  outbox: 'outbox',
};

/**
 * Folders whose items have no reliable received time (gromox: EWS-sent copies sort last).
 * UPSTREAM-WORKAROUND(gx-07): sort these by created time; remove when SendAndSaveCopy copies carry a submit/delivery time
 */
const SORT_BY_CREATED = new Set(['sentitems', 'drafts', 'outbox']);

/** Content-ID of a quoted picture shown from `GET …/items/{id}/inline/{contentId}` (see inlineImageUrl). */
export function quotedImageContentId(src: string): string | undefined {
  const match = /\/mailbox\/items\/[^/?#]+\/inline\/([^/?#]+)$/.exec(src);
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

/**
 * Composer body → API body. Rich text goes out as mail-ready HTML with its pictures as inline
 * images (cid:) and the quoted original's pictures as cid: references (the server attaches them);
 * plain text as text. The server sanitizes HTML and adds the text alternative.
 */
export function toApiBody(body: RichTextValue): { body: { type: 'html' | 'text'; content: string }; inlineImages: InlineImage[] } {
  if (body.format === 'text') return { body: { type: 'text', content: body.content }, inlineImages: [] };
  const { html, inlineImages } = prepareOutgoingHtml(body.content, { quotedImageContentId });
  return { body: { type: 'html', content: html }, inlineImages };
}

const uploads = (ids: readonly string[] | undefined) => (ids && ids.length > 0 ? { uploadIds: [...ids] } : {});

function toSession(info: SessionInfo): Session {
  return { username: info.username, displayName: info.displayName };
}

const toAddress = (address: MailAddress | null | undefined): Address => ({ name: address?.name ?? '', address: address?.email ?? '' });
const toApiAddress = ({ name, address }: Address): NonNullable<NewMessage['to']>[number] => (name && name !== address ? { name, email: address } : { email: address });

export function toFolder(folder: MailFolder): Folder {
  const wellKnown = folder.wellKnown ? WELL_KNOWN[folder.wellKnown] : undefined;
  return {
    id: folder.mailboxRoot || folder.mailboxContext ? folder.id : folder.wellKnown ?? folder.id,
    nativeId: folder.id,
    wellKnown,
    name: folder.name,
    depth: folder.depth,
    unread: folder.unreadCount,
    total: folder.totalCount,
    ...(folder.mailboxRoot && { mailboxRoot: true }),
    ...(folder.mailboxRoot && { mailboxAddress: mailboxGroupAddress(folder.id) }),
    ...(folder.mailboxContext && { mailboxContext: true }),
  };
}

/** Presentation only: this is the wrapper's explicitly non-EWS group ID, never a native folder/item ID. */
function mailboxGroupAddress(id: string): string | undefined {
  if (!id.startsWith('shared-root:')) return undefined;
  try {
    const bytes = Uint8Array.from(atob(id.slice('shared-root:'.length).replace(/-/g, '+').replace(/_/g, '/')), character => character.charCodeAt(0));
    const address = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return /^[^@\s]+@[^@\s]+$/.test(address) ? address : undefined;
  } catch { return undefined; }
}

export function toSummary(item: ItemSummary): MessageSummary {
  return {
    id: item.id,
    from: toAddress(item.from),
    subject: item.subject,
    preview: item.preview ?? '',
    receivedAt: item.receivedAt ?? '',
    unread: !item.isRead,
    flagged: item.flagged,
    hasAttachments: item.hasAttachments,
  };
}

/**
 * Attachments that open as a nested message: attached items, and .eml files (forward as attachment
 * sends the original as a file, gx-27). The server checks the content before parsing it.
 */
function isMessageAttachment(a: MessageDetail['attachments'][number]): boolean {
  return a.kind === 'item' || a.contentType === 'message/rfc822' || /\.eml$/i.test(a.name);
}

export function toMessage(detail: MessageDetail): Message {
  const html = detail.body.type === 'html' ? detail.body.content : plainTextToMailHtml(detail.body.content);
  return {
    ...toSummary(detail),
    receivedAt: detail.receivedAt ?? detail.sentAt ?? '',
    sentAt: detail.sentAt ?? undefined,
    text: detail.body.type === 'text' ? detail.body.content : undefined,
    to: detail.to.map(toAddress),
    cc: detail.cc.map(toAddress),
    replyTo: detail.replyTo.map(toAddress),
    html,
    // Pictures the body shows (cid:) are not listed again; gromox never sets IsInline (dossier 14).
    attachments: detail.attachments
      .filter((attachment) => !(attachment.contentId && html.includes(`cid:${attachment.contentId}`)))
      .map((attachment) => ({ id: attachment.id, name: attachment.name, kind: isMessageAttachment(attachment) ? 'item' : 'file', size: attachment.size })),
    ...(detail.meeting !== undefined && { meeting: detail.meeting }),
  };
}

export function toAttachedMessage(message: ApiAttachedMessage): AttachedMessage {
  return {
    subject: message.subject,
    from: toAddress(message.from),
    to: message.to.map(toAddress),
    cc: message.cc.map(toAddress),
    sentAt: message.sentAt ?? '',
    html: message.body.type === 'html' ? message.body.content : plainTextToMailHtml(message.body.content),
    attachments: message.attachments.map(({ name, size }) => ({ name, size })),
  };
}

export function createRealApi(baseUrl: string, fetchImpl?: typeof fetch): WebmailApi {
  const http = createHttpClient({ app: 'webmail', baseUrl, fetch: fetchImpl });
  const client = createWebmailClient(http);

  return {
    ...createRealCalendarExtra(client),
    groupware: createGroupwareClient(http),
    mailCrypto: createMailCryptoClient(http),
    mfa: createMfaClient(http),
    identity: createIdentityClient(http),
    ...createRealQuarantineMe(client),
    ...createRealCalendarFeeds(client),
    login: async (username, password) => { const session = toSession(await client.login(username, password)); markOwnedSession(true); return session; },
    logout: () => logoutBrowser(http, () => client.logout(), async () => { markOwnedSession(false); await expirePwa(); }),
    restoreSession: async () => {
      try {
        const info = await client.session();
        if (info) markOwnedSession(true);
        else await expireOwnedSession();
        return info && toSession(info);
      } catch (error) {
        if (error instanceof ApiError && (error.isUnauthorized || error.code === 'mfa_required')) await expireOwnedSession();
        throw error;
      }
    },
    onSessionExpired: listener => {
      const unsubscribe = http.onUnauthorized(() => { void expireOwnedSession(); listener(); });
      const peer = onPeerExpiry(listener);
      return () => { unsubscribe(); peer(); };
    },
    runtime: createPublicClient(http),

    // Only mail folders: calendar, contacts and tasks get their own modules.
    listFolders: async () => (await client.folders()).folders.filter((folder) => folder.kind === 'mail').map(toFolder),
    listPublicFolders: (query) => client.myPublicFolders(query),
    async listPublicMessages(folderId, offset = 0) {
      const page = await client.myPublicFolderItems(folderId, { offset, limit: PAGE_SIZE, sortBy: 'received' });
      return { items: page.items.map(toSummary), total: page.total, nextOffset: page.nextOffset };
    },
    getPublicMessage: async (folderId, itemId) => toMessage(await client.myPublicFolderItem(folderId, itemId, 'html')),

    async listMessages(folderId, offset = 0) {
      const page = await client.items(folderId, { offset, limit: PAGE_SIZE, sortBy: SORT_BY_CREATED.has(folderId) ? 'created' : 'received' });
      return { items: page.items.map(toSummary), total: page.total, nextOffset: page.nextOffset };
    },

    async searchMessages(input) {
      const result = await client.searchMessages(input);
      return { ...result, items: result.items.map((item) => ({ ...toSummary(item), folderId: item.folderId, folderName: item.folderName })) };
    },

    getMessage: async (id) => toMessage(await client.item(id, 'html')),
    setRead: (id, read) => client.updateItem(id, { isRead: read }),
    deleteMessage: (id) => client.deleteItem(id),

    // Batch actions (DESIGN.md §18): the server answers per item; failed items become one error.
    setFlag: (ids, flagged) => inBatches(ids, (chunk) => client.updateItems({ itemIds: chunk, flagged })),
    moveMessages: (ids, folderId) => inBatches(ids, (chunk) => client.moveItems(chunk, folderId)),
    copyMessages: (ids, folderId) => inBatches(ids, (chunk) => client.copyItems(chunk, folderId)),
    createFolder: async (parentId, name) => {
      await client.createFolder(parentId, name);
    },
    renameFolder: (id, name) => client.renameFolder(id, name),
    deleteFolder: (id) => client.deleteFolder(id),
    markAllRead: async (folderId) => {
      await client.markAllRead(folderId);
    },
    emptyFolder: (folderId) => client.emptyFolder(folderId),
    importMessage: async (folderId, file, options) => {
      await client.importMessage(folderId, file, file.name || 'message.eml', options);
    },

    send: ({ from, to, cc, bcc, subject, body, attachItemIds = [], uploadIds }) =>
      client.send({
        ...fromField(from),
        subject,
        ...toApiBody(body),
        to: to.map(toApiAddress),
        cc: cc.map(toApiAddress),
        bcc: bcc.map(toApiAddress),
        ...(attachItemIds.length > 0 && { attachItemIds: [...attachItemIds] }),
        ...uploads(uploadIds),
      }),
    // The body holds the quoted history (composer), so the server does not quote again.
    reply: (id, { from, body, replyAll, uploadIds }) => client.reply(id, { ...fromField(from), ...toApiBody(body), replyAll, ...uploads(uploadIds) }),
    forward: (id, { from, to, cc, bcc, body, originalAttachmentIds, uploadIds }) =>
      client.forward(id, {
        ...fromField(from),
        ...toApiBody(body),
        to: to.map(toApiAddress),
        cc: cc.map(toApiAddress),
        bcc: bcc.map(toApiAddress),
        originalAttachmentIds: [...originalAttachmentIds],
        ...uploads(uploadIds),
      }),

    attachmentLimits: () => client.attachmentLimits(),
    saveDraft: (input) => client.saveDraft(input),
    replaceDraft: (id, input) => client.replaceDraft(id, input),
    sendDraft: (id, input) => client.sendDraft(id, input),
    getDraft: async (id) => {
      const draft = await client.draft(id);
      if (draft.body.type !== 'html') return draft;
      const content = await loadDraftHtml(draft.body.content, async cid => {
        const response = await fetch(client.inlineImageUrl(id, cid), { credentials: 'same-origin' });
        if (!response.ok) throw new ApiError(response.status, 'draft_image_unavailable', 'Draft image unavailable');
        return response.blob();
      });
      return { ...draft, body: { ...draft.body, content } };
    },
    uploadAttachment: (file, options) => client.upload(file, file.name || 'attachment', options),
    deleteUpload: (id) => client.deleteUpload(id),
    inlineImageUrl: (messageId, contentId) => client.inlineImageUrl(messageId, contentId),
    attachmentDownloadUrl: (messageId, attachmentId) => client.attachmentUrl(messageId, attachmentId),

    suggestRecipients: async (query, signal) => (await client.suggestRecipients(query, 10, signal)).suggestions,
    resolveRecipients: async (entries) => (await client.resolveRecipients([...entries])).results,
    removeSuggestion: (key) => client.removeSuggestion(key),
    groupMembers: async (key) => (await client.groupMembers(key)).members,
    addressBook: (q, source, cursor, signal) => client.addressBook({ q, source, limit: 50, ...(cursor && { cursor }) }, signal),
    identities: async () => (await client.identities()).identities.map(({ email, name, kind }) => ({ email, name, kind })),
    photoUrl: (email) => (email ? client.photoUrl(personKey(email), 96) : undefined),

    mySignatures: () => client.mySignatures(),
    mySignature: (id) => client.mySignature(id),
    createSignature: (input) => client.createMySignature(input),
    updateSignature: (id, input) => client.updateMySignature(id, input),
    deleteSignature: (id) => client.deleteMySignature(id),
    setSignatureDefaults: (defaults) => client.setMySignatureDefaults(defaults),

    myInboxRules: () => client.myInboxRules(),
    saveMyInboxRules: input=>client.saveMyInboxRules(input),
    myAutomaticReplies: () => client.myAutomaticReplies(),
    setAutomaticReplies: (input) => client.setMyAutomaticReplies(input),
    myTypedList: (id) => client.myTypedList(id),
    putMyPasswordListKey: (id, keyId, input) => client.putMyPasswordListKey(id, keyId, input),
    deleteMyPasswordListKey: (id, keyId, input) => client.deleteMyPasswordListKey(id, keyId, input),
    setMyPasswordListShares: (id, input) => client.setMyPasswordListShares(id, input),
    myShareGroups: () => client.myShareGroups(),
    myScopedLists: () => client.myScopedLists(),
    myScopedListEntries: (listId) => client.myScopedListEntries(listId),
    addMyScopedListEntries: (listId, values) => client.addMyScopedListEntries(listId, values),
    removeMyScopedListEntries: (listId, values) => client.removeMyScopedListEntries(listId, values),
    importMyScopedList: (listId, csv) => client.importMyScopedList(listId, csv),

    getSource: (id) => client.source(id, SOURCE_VIEW_BYTES),
    sourceDownloadUrl: (id) => client.sourceDownloadUrl(id),
    getAnalysis: (id) => client.analysis(id),
    getAttachedMessage: async (id, attachmentId) => toAttachedMessage(await client.attachedMessage(id, attachmentId)),

    listCalendars: async () => (await client.calendars()).calendars,
    addCalendar: (address, kind) => client.addCalendar({ address, ...(kind && { kind }) }),
    createOwnCalendar: (name) => client.addCalendar({ kind: 'own', name }),
    updateCalendar: (id, changes) => client.updateCalendar(id, changes),
    removeCalendar: (id) => client.removeCalendar(id),
    listEvents: async (calendarIds, start, end) => (await client.calendarEvents({ calendars: calendarIds.join(','), start, end })).results,
    getEvent: (id) => client.calendarEvent(id),
    createEvent: (calendarId, input) => client.createCalendarEvent(input, calendarId),
    updateEvent: (id, changes, scope, notify) => client.updateCalendarEvent(id, changes, scope, notify),
    deleteEvent: (id, { scope, notify, comment } = {}) =>
      client.deleteCalendarEvent(id, { ...(scope && { scope }), ...(notify !== undefined && { notify: notify ? 'true' : 'false' }), ...(comment && { comment }) }),
    respondToEvent: async (id, input) => (await client.respondToEvent(id, input)).eventId,
    freeBusy: (attendees, start, end) => client.freeBusy({ attendees: [...attendees], start, end }),
    schedulingSuggestions: input => client.schedulingSuggestions(input),
    roomLists: () => client.roomLists(),
    findRooms: input => client.findRooms(input),
    listRooms: async () => (await client.rooms()).rooms,
    myAttendantSettings: () => client.myAttendantSettings(),
    setMyAttendantSettings: (input) => client.setMyAttendantSettings(input),
    bookingRequests: async () => (await client.bookingRequests()).requests,
    decideBookingRequest: (id, verdict, comment) => (verdict === 'approve' ? client.approveBookingRequest(id, comment) : client.declineBookingRequest(id, comment)),
    calendarPermissions: (calendarId) => client.calendarPermissions(calendarId),
    setCalendarPermissions: (calendarId, input) => client.setCalendarPermissions(input, calendarId),
  };
}

/** Items per batch request (platform API `MAX_BATCH_ITEMS`). */
export const BATCH_SIZE = 500;

/**
 * Runs a per-item batch call in chunks of BATCH_SIZE and throws one `partial_failure` error
 * (counts in the body) when any item failed; the app reports it like any other failed action.
 */
async function inBatches(ids: readonly string[], call: (chunk: string[]) => Promise<ItemResults>): Promise<void> {
  let failed = 0;
  for (let start = 0; start < ids.length; start += BATCH_SIZE) {
    const { items } = await call(ids.slice(start, start + BATCH_SIZE));
    failed += items.filter((item) => !item.ok).length;
  }
  if (failed > 0) {
    const body: PartialFailure = { failed, total: ids.length };
    throw new ApiError(200, PARTIAL_FAILURE, `${failed} of ${ids.length} items failed`, { body });
  }
}

const fromField =(from: string | undefined) => (from ? { from } : {});

/** Server key of an address: base64url of the lower-cased UTF-8 address (see platform-api recipients/parse.ts). */
export function personKey(email: string): string {
  const bytes = new TextEncoder().encode(email.toLowerCase());
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
